import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMapMarkerAlt, faPhone } from '@fortawesome/free-solid-svg-icons'
import GPMapSelection from '../GPMapSelection'
import GpAddressForm from '../GpAddressForm'
import useAxiosPrivate from '../../hooks/useAxiosPrivate'
import useAuth from '../../hooks/useAuth'
import { toast } from 'sonner'

const GPDetailsSection = ({
  selectedGP,
  setSelectedGP,
  showGpMap,
  setShowGpMap,
  enterDetailsManually,
  setEnterDetailsManually,
  manualDetails,
  setManualDetails,
  isNotRegisteredWithGP,
  setIsNotRegisteredWithGP,
  handleClearSelectedGP,
  handleEditSelectedGP,
  showTitle = true,
  profileData = null,
  userInfo = null,
  setUserInfo = null,
  dependant = false, // new prop
  alterText = false,
  setGpCleared = null,
  setDependant = null,
}) => {
  const axios = useAxiosPrivate()
  const { auth, setAuth, setShowLoader } = useAuth()

  const updateProfileImmediately = async (gpDetails) => {
    try {
      setShowLoader(true)
      if (!setUserInfo || !userInfo || !auth?.user?.id) {
        console.error('Profile update skipped - missing required data or context')
        return
      }
      const updatedUserInfo = {
        ...userInfo,
        gp: gpDetails,
      }
      setUserInfo(updatedUserInfo)

      const data = {
        info: updatedUserInfo,
        id: dependant ? dependant.id : auth?.user?.id,
      }

      const response = await axios.post('/user/gp/update', data)
      console.log(auth?.user)
      if (!response.data.error) {
        let curDeps = auth?.user?.dependants
        curDeps[Object.keys(auth?.user?.dependants).find((key) => auth?.user?.dependants[key].id === dependant.id)] = response?.data?.result
        await setAuth((prev) => ({
          ...prev,
          user: {
            ...prev.user,
            dependants: curDeps
          },
        }))
        setDependant((prev) => ({
          ...prev,
          user_info: {
            ...prev.user_info,
            gp: gpDetails
          }
        }))
        toast.success('GP details updated successfully!')
      }
      setShowLoader(false)
    } catch (error) {
      setShowLoader(false)
      console.error('Error updating GP details:', error)
      toast.error('Failed to update GP details. Please try again.')
    }
  }

  return (
    <>
      {showTitle && (
        <div className="sm:col-span-6">
          <h2 className="text-xl font-medium">GP Details</h2>
        </div>
      )}

      <div className="sm:col-span-6">
        <section>
          {showGpMap ? (
            <GPMapSelection
              onHideMap={() => setShowGpMap(false)}
              selectedGP={selectedGP}
              setSelectedGP={setSelectedGP}
              onChoose={async (gpDetails) => {
                setSelectedGP(gpDetails)
                setIsNotRegisteredWithGP(false)
                setEnterDetailsManually(false)
                setShowGpMap(false)
                await updateProfileImmediately(gpDetails)
                if (typeof setGpCleared === 'function') {
                  setGpCleared(false)
                }
              }}
            />
          ) : (
            <div>
              {selectedGP && Object.keys(selectedGP)?.length ? (
                <div className="bg-white p-4 rounded-md shadow-md border border-gray-300 relative">
                  <h3 className="font-semibold text-portal-dark-blue mb-2">
                    {selectedGP.name || selectedGP.surgeryName || 'GP Surgery'}
                  </h3>

                  <div className="mb-4 flex items-start text-gray-500">
                    <FontAwesomeIcon
                      icon={faMapMarkerAlt}
                      className="text-gray-500 mt-1 mr-2"
                    />
                    <div>
                      {selectedGP.address &&
                        (typeof selectedGP.address === 'string' ? (
                          <p>{selectedGP.address}</p>
                        ) : (
                          <p>
                            {[
                              selectedGP.address?.addressLine1,
                              selectedGP.address?.addressLine2,
                              selectedGP.address?.city,
                              selectedGP.address?.postcode,
                              selectedGP.address?.country,
                            ]
                              .filter(Boolean)
                              .join(', ')}
                          </p>
                        ))}
                      {selectedGP.addressLine1 &&
                        <p>
                          {[
                            selectedGP?.addressLine1,
                            selectedGP?.addressLine2,
                            selectedGP?.city,
                            selectedGP?.postcode,
                            selectedGP?.country,
                          ]
                            .filter(Boolean)
                            .join(', ')}
                        </p>
                      }
                      {!selectedGP.address && selectedGP.postcode && (
                        <p>{selectedGP.postcode}</p>
                      )}
                    </div>
                  </div>

                  {selectedGP.phone && (
                    <p className="mt-4 flex items-center gap-2 text-sm text-gray-500">
                      <FontAwesomeIcon icon={faPhone} />
                      {selectedGP.phone}
                    </p>
                  )}

                  <div className="absolute bottom-3 right-3 flex gap-4 text-sm text-blue-600 cursor-pointer select-none">
                    <span
                      onClick={handleClearSelectedGP}
                      className="hover:underline"
                    >
                      Clear
                    </span>
                    <span
                      onClick={handleEditSelectedGP}
                      className="hover:underline"
                    >
                      Edit
                    </span>
                  </div>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      if (!isNotRegisteredWithGP) {
                        setShowGpMap(true)
                      }
                    }}
                    disabled={isNotRegisteredWithGP}
                    className={`w-56 h-12 px-6 py-3 rounded-md text-sm font-bold text-white flex justify-center items-center ${isNotRegisteredWithGP
                        ? 'bg-gray-500 hover:bg-gray-400 cursor-not-allowed'
                        : 'bg-hub-primary hover:bg-hub-primary-hover'
                      }`}
                  >
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2" />{' '}
                    Choose on map
                  </button>

                  {!enterDetailsManually && !isNotRegisteredWithGP && (
                    <div className="mt-2 mb-2">
                      <button
                        type="button"
                        className="underline"
                        onClick={() => setEnterDetailsManually(true)}
                      >
                        Enter Details Manually
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {enterDetailsManually && (
            <GpAddressForm
              manualDetails={manualDetails}
              setManualDetails={setManualDetails}
            />
          )}

          <div className="mt-2">
            <label className="text-sm flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isNotRegisteredWithGP}
                onChange={() => {
                  setIsNotRegisteredWithGP(!isNotRegisteredWithGP)
                  setEnterDetailsManually(false)
                  setShowGpMap(false)
                  setSelectedGP(null)
                }}
              />
              {dependant
                ? `My dependant is not registered with a GP surgery${alterText ? ' / do not wish to disclose at this time' : ''}`
                : `I am not registered with a GP surgery${alterText ? ' / do not wish to disclose at this time' : ''}`}
            </label>
          </div>
        </section>
      </div>
    </>
  )
}

export default GPDetailsSection
